import React, { Component, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import FooterContainer from '../FooterContainer/FooterContainer';

import { omit } from '../../util/common';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, IconLocation, ModalInMobile, NamedRedirect, Page } from '../../components';
import LocationAutocompleteInput from '../../components/LocationAutocompleteInput/LocationAutocompleteInput';
import BookingDateRangeFilter from './BookingDateRangeFilter/BookingDateRangeFilter';
import GuestPicker from './GuestPicker/GuestPicker';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { logout } from '../../ducks/auth.duck';
import { setActiveListing, searchSectionListings, clearSectionResults } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchFilterBar from './SearchFilterBar/SearchFilterBar';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import GroupedSearchResults from './GroupedSearchResults/GroupedSearchResults';
import SearchPageTopbar from './SearchPageTopbar/SearchPageTopbar';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 1024; // Search is in modal on mobile layout

// ── MobileSearchBar helpers ───────────────────────────────────────────────────

const formatMobileDateRange = dates => {
  if (!dates) return null;
  const [start, end] = dates.split(',');
  if (!start) return null;
  try {
    const s = new Date(start + 'T12:00:00');
    const e = end ? new Date(end + 'T12:00:00') : null;
    const startStr = s.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!e) return startStr;
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    const endStr = e.toLocaleDateString('en-US', sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
    return `${startStr} – ${endStr}`;
  } catch {
    return null;
  }
};

/**
 * Fixed mobile search bar shown just below the BoatersNetwork logo.
 * Renders the Where / When / Who pill — only visible on mobile (<768px).
 */
const MobileSearchBar = ({ address, validQueryParams, onLocationSearch, onSubmit, onGuestSearch, intl }) => {
  const [isLocationSheetOpen, setIsLocationSheetOpen] = useState(false);
  const [isDateSheetOpen, setIsDateSheetOpen] = useState(false);
  const [locationValue, setLocationValue] = useState({
    search: address || '',
    predictions: [],
    selectedPlace: null,
  });

  useEffect(() => {
    setLocationValue(prev => ({ ...prev, search: address || '', selectedPlace: null }));
  }, [address]);

  const formattedDates = formatMobileDateRange(validQueryParams?.dates);

  return (
    <>
      <div className={css.mobileSearchBar}>
        <div className={css.msbPill}>
          <button
            type="button"
            className={css.msbLocationBtn}
            onClick={() => setIsLocationSheetOpen(true)}
            aria-label="Edit location"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: '#6B7280' }}>
              <path d="M8 1.5C5.515 1.5 3.5 3.515 3.5 6c0 3.5 4.5 9 4.5 9s4.5-5.5 4.5-9c0-2.485-2.015-4.5-4.5-4.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              <circle cx="8" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <span className={css.msbLocationText}>{address || 'Where'}</span>
          </button>
          <span className={css.msbDivider} aria-hidden="true" />
          <button
            type="button"
            className={css.msbDatesBtn}
            onClick={() => setIsDateSheetOpen(true)}
            aria-label={formattedDates ? 'Edit dates' : 'Add dates'}
          >
            {formattedDates || 'When'}
          </button>
          {onGuestSearch && (
            <>
              <span className={css.msbDivider} aria-hidden="true" />
              <GuestPicker
                onSearch={onGuestSearch}
                triggerClassName={css.msbGuestBtn}
                triggerActiveClassName={css.msbGuestBtnActive}
                sheetMode
              />
            </>
          )}
        </div>
      </div>

      {isDateSheetOpen && (
        <>
          <div className={css.msbBackdrop} onClick={() => setIsDateSheetOpen(false)} />
          <div className={css.msbDateSheet} role="dialog" aria-label="Select dates">
            <div className={css.msbSheetHandle} />
            <div className={css.msbSheetTitleRow}>
              <div className={css.msbSheetTitle}>Select dates</div>
              <button type="button" className={css.msbSheetCloseBtn} onClick={() => setIsDateSheetOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className={css.msbDateSheetContent}>
              <BookingDateRangeFilter
                id="msb-dates"
                queryParamNames={['dates']}
                initialValues={validQueryParams || {}}
                showAsPopup={false}
                liveEdit
                onSubmit={params => {
                  onSubmit(params);
                  if (params.dates) setIsDateSheetOpen(false);
                }}
                intl={intl}
              />
            </div>
          </div>
        </>
      )}

      {isLocationSheetOpen && (
        <>
          <div className={css.msbBackdrop} onClick={() => setIsLocationSheetOpen(false)} />
          <div className={css.msbLocationSheet} role="dialog" aria-label="Search location">
            <div className={css.msbSheetHandle} />
            <div className={css.msbSheetTitleRow}>
              <div className={css.msbSheetTitle}>Location</div>
              <button type="button" className={css.msbSheetCloseBtn} onClick={() => setIsLocationSheetOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className={css.msbLocationSheetContent}>
              <LocationAutocompleteInput
                id="msb-location"
                rootClassName={css.msbLocationInputRoot}
                inputClassName={css.msbLocationInput}
                predictionsClassName={css.msbLocationPredictions}
                iconClassName={css.msbLocationIconWrap}
                CustomIcon={() => <IconLocation rootClassName={css.msbLocationIconSvg} />}
                placeholder="City, lake, or region"
                useDarkText={true}
                disableKeywordSuggestions={true}
                geocoderTypes={['place', 'region', 'country']}
                closeOnBlur={false}
                hideErrorMessage
                autoFocus
                input={{
                  name: 'msb-location',
                  value: locationValue,
                  onChange: value => {
                    setLocationValue(value);
                    if (value?.selectedPlace) {
                      const { search } = value;
                      const { origin, bounds } = value.selectedPlace;
                      if (onLocationSearch) {
                        onLocationSearch({
                          address: search,
                          bounds,
                          ...(origin ? { origin: `${origin.lat},${origin.lng}` } : {}),
                        });
                      }
                      setIsLocationSheetOpen(false);
                    }
                  },
                  onBlur: () => {},
                  onFocus: () => {},
                }}
                meta={{ valid: true, invalid: false, touched: false, error: undefined }}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
const SEARCH_WITH_MAP_DEBOUNCE = 300; // Little bit of debounce before search is initiated.
const MAP_VISIBLE_STORAGE_KEY = 'searchPage.isMapVisible';

// Primary filters have their content in dropdown-popup.
// With this offset we move the dropdown to the left a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;

const getSelectedSecondaryFiltersCount = (
  validQueryParams,
  filterConfigs,
  customSecondaryFilters
) => {
  const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);
  const potentialSecondaryFilters = hasSecondaryFilters
    ? validFilterParams(validQueryParams, {
        ...filterConfigs,
        listingFieldsConfig: customSecondaryFilters,
      })
    : {};

  const relevantQueryParamNames = customSecondaryFilters.map(f => {
    return f.scope === 'public' ? `pub_${f.key}` : f.key;
  });
  const pickRelevant = name => relevantQueryParamNames.includes(name);
  const selectedSecondaryFilters = Object.keys(potentialSecondaryFilters).filter(pickRelevant);
  return selectedSecondaryFilters?.length;
};

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    const storedMapVisible =
      typeof window !== 'undefined'
        ? localStorage.getItem(MAP_VISIBLE_STORAGE_KEY) !== 'false'
        : true;

    this.state = {
      isSearchMapOpenOnMobile: false,
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isSecondaryFiltersOpen: false,
      isMapVisible: storedMapVisible,
      shouldOpenFilterPanel: false,
      viewAllType: null,
    };

    this.onMapMoveEnd = debounce(this.onMapMoveEnd.bind(this), SEARCH_WITH_MAP_DEBOUNCE);
    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.toggleMapVisibility = this.toggleMapVisibility.bind(this);

    // Filter functions
    this.applyFilters = this.applyFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Mobile bottom sheet drag-to-expand
    this._sheetRef = null;
    this._sheetBodyRef = null;
    this._dragStartY = null;
    this._dragStartTopPx = null;
    this._isDraggingSheet = false;
    this.onSheetHeaderTouchStart = this.onSheetHeaderTouchStart.bind(this);
    this.onSheetHeaderTouchMove = this.onSheetHeaderTouchMove.bind(this);
    this.onSheetHeaderTouchEnd = this.onSheetHeaderTouchEnd.bind(this);
    this.onSheetBodyTouchStart = this.onSheetBodyTouchStart.bind(this);
    this.onSheetBodyTouchMove = this.onSheetBodyTouchMove.bind(this);
    this.onSheetBodyTouchEnd = this.onSheetBodyTouchEnd.bind(this);
  }

  _startSheetDrag(clientY) {
    if (!this._sheetRef) return;
    this._dragStartY = clientY;
    this._dragStartTopPx = this._sheetRef.getBoundingClientRect().top;
    this._isDraggingSheet = true;
  }

  _moveSheet(clientY) {
    if (!this._isDraggingSheet || this._dragStartY == null || !this._sheetRef) return;
    const dy = clientY - this._dragStartY;
    const newTop = Math.max(0, Math.min(window.innerHeight * 0.85, this._dragStartTopPx + dy));
    this._sheetRef.style.top = newTop + 'px';
  }

  _endSheetDrag() {
    if (!this._sheetRef || !this._isDraggingSheet) return;
    const currentTopPx = this._sheetRef.getBoundingClientRect().top;
    const collapsedPx = window.innerHeight * 0.75;
    const expandedPx = window.innerHeight * 0.08;
    // Snap up (expand) if dragged above midpoint between snap points, else snap down (collapse)
    const midPx = (collapsedPx + expandedPx) / 2;
    const snapTopPx = currentTopPx < midPx ? expandedPx : collapsedPx;
    this._sheetRef.style.transition = 'top 0.25s ease';
    this._sheetRef.style.top = snapTopPx + 'px';
    setTimeout(() => {
      if (this._sheetRef) this._sheetRef.style.transition = '';
    }, 260);
    this._dragStartY = null;
    this._dragStartTopPx = null;
    this._isDraggingSheet = false;
  }

  // Header handlers — always drag the sheet
  onSheetHeaderTouchStart(e) {
    e.preventDefault();
    this._startSheetDrag(e.touches[0].clientY);
  }

  onSheetHeaderTouchMove(e) {
    e.preventDefault();
    this._moveSheet(e.touches[0].clientY);
  }

  onSheetHeaderTouchEnd(e) {
    e.preventDefault();
    this._endSheetDrag();
  }

  // Body handlers — only take over the sheet drag when at scroll top dragging downward
  onSheetBodyTouchStart(e) {
    this._dragStartY = e.touches[0].clientY;
    this._isDraggingSheet = false;
  }

  onSheetBodyTouchMove(e) {
    if (!this._sheetRef || !this._sheetBodyRef) return;
    const dy = e.touches[0].clientY - this._dragStartY;
    const atTop = this._sheetBodyRef.scrollTop === 0;

    if (!this._isDraggingSheet) {
      // Initiate sheet drag only when at scroll top AND dragging downward
      if (atTop && dy > 8) {
        this._isDraggingSheet = true;
        this._dragStartTopPx = this._sheetRef.getBoundingClientRect().top;
      } else {
        return; // let scroll handle it
      }
    }

    // Dragging the sheet — prevent scroll takeover
    e.preventDefault();
    this._moveSheet(e.touches[0].clientY);
  }

  onSheetBodyTouchEnd() {
    if (this._isDraggingSheet) {
      this._endSheetDrag();
    } else {
      this._dragStartY = null;
    }
  }

  // Callback to determine if new search is needed
  // when map is moved by user or viewport has changed
  onMapMoveEnd(viewportBoundsChanged, data) {
    const { viewportBounds, viewportCenter } = data;
    const { params: currentPathParams } = this.props;

    const routes = this.props.routeConfiguration;
    const searchPagePath = currentPathParams.listingType
      ? pathByRouteName('SearchPageWithListingType', routes, currentPathParams)
      : pathByRouteName('SearchPage', routes);
    const currentPath =
      typeof window !== 'undefined' && window.location && window.location.pathname;

    // When using the ReusableMapContainer onMapMoveEnd can fire from other pages than SearchPage too
    const isSearchPage = currentPath === searchPagePath;

    // If mapSearch url param is given
    // or original location search is rendered once,
    // we start to react to "mapmoveend" events by generating new searches
    // (i.e. 'moveend' event in Mapbox and 'bounds_changed' in Google Maps)
    if (viewportBoundsChanged && isSearchPage) {
      const { history, location, config } = this.props;
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig } = config?.search || {};
      const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
        activeListingTypes,
        currentPathParams,
      };

      // parse query parameters, including a custom attribute named category
      // when onMapMoveEnd is called, pagination needs to be reset.
      const { address, bounds, mapSearch, page, ...rest } = parse(location.search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
      });

      const originMaybe = isOriginInUse(this.props.config) ? { origin: viewportCenter } : {};
      const dropNonFilterParams = false;

      const searchParams = {
        address,
        ...originMaybe,
        bounds: viewportBounds,
        mapSearch: true,
        ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
      };

      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routes, location);

      history.push(createResourceLocatorString(routeName, routes, pathParams, searchParams));
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  toggleMapVisibility() {
    this.setState(prev => {
      const next = !prev.isMapVisible;
      if (typeof window !== 'undefined') {
        localStorage.setItem(MAP_VISIBLE_STORAGE_KEY, String(next));
      }
      return { isMapVisible: next };
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // When the map transitions from hidden to visible, trigger a resize so the map
    // library re-paints its canvas and re-renders price markers correctly.
    if (!prevState.isMapVisible && this.state.isMapVisible) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('resize'));
      }
    }

    // Prevent pull-to-refresh while mobile map sheet is open
    if (typeof document !== 'undefined') {
      if (this.state.isSearchMapOpenOnMobile && !prevState.isSearchMapOpenOnMobile) {
        document.body.style.overscrollBehaviorY = 'none';
      } else if (!this.state.isSearchMapOpenOnMobile && prevState.isSearchMapOpenOnMobile) {
        document.body.style.overscrollBehaviorY = '';
      }
    }
  }

  componentWillUnmount() {
    if (typeof document !== 'undefined') {
      document.body.style.overscrollBehaviorY = '';
    }
  }

  // Apply the filters by redirecting to SearchPage with new filters.
  applyFilters() {
    const { history, routeConfiguration, config, params: currentPathParams, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const searchParams = { ...urlQueryParams, ...this.state.currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, search));
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    this.setState({ currentQueryParams: {} });
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, location } = this.props;

    // Reset state
    this.setState({ currentQueryParams: {} });

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    // Navigate to the search page with completely empty query params — clean slate.
    // Using omit(urlQueryParams, filterQueryParamNames) is not sufficient because
    // pub_categoryLevel1/2/3 are excluded from filterQueryParamNames when
    // categoryFilter.enabled is false, causing category params to survive the reset.
    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, {})
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};

        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

        return {
          currentQueryParams: omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              ...datesAndSeatsMaybe,
              address,
              bounds,
            },
            filterConfigs
          ),
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );

          history.push(
            createResourceLocatorString(routeName, routeConfiguration, pathParams, search)
          );
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  render() {
    const {
      intl,
      listings = [],
      history,
      location,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      activeListingId,
      onActivateListing,
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
      isAuthenticated,
      notificationCount,
      currentUserHasListings,
      onLogout,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const { listingType: listingTypePathParam } = currentPathParams;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig, mainSearch } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
      mainSearch,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigs,
      sortConfig,
      mainSearch,
      isOriginInUse(config)
    );

    const validQueryParams = urlQueryParams;

    const isWindowDefined = typeof window !== 'undefined';
    const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
    // On desktop the map stays mounted regardless of isMapVisible (hidden via CSS only),
    // so toggling doesn't cause a remount and lose markers/state.
    // On mobile it only mounts when explicitly opened.
    const shouldShowSearchMap = !isMobileLayout || this.state.isSearchMapOpenOnMobile;

    const isKeywordSearch = isMainSearchTypeKeywords(config);
    // categoryLevel and listingType are replaced by the ListingTypeChipBar — exclude from filter panels
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availablePrimaryFilters = [
      ...customPrimaryFilters,
      ...builtInFilters,
    ];
    const availableFilters = [
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

    // Selected aka active filters
    const selectedFilters = validQueryParams;
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
      : keysOfSelectedFilters.length;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

    const selectedSecondaryFiltersCount = getSelectedSecondaryFiltersCount(
      validQueryParams,
      filterConfigs,
      customSecondaryFilters
    );

    const isSecondaryFiltersOpen = !!hasSecondaryFilters && this.state.isSecondaryFiltersOpen;
    const propsForSecondaryFiltersToggle = hasSecondaryFilters
      ? {
          isSecondaryFiltersOpen: this.state.isSecondaryFiltersOpen,
          toggleSecondaryFiltersOpen: isOpen => {
            this.setState({ isSecondaryFiltersOpen: isOpen, currentQueryParams: {} });
          },
          selectedSecondaryFiltersCount,
        }
      : {};

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = showCreateListingLinkForUser(config, currentUser);

    // Shared handlers used by both MobileSearchBar and SearchFilterBar
    const onLocationSearch = params => {
      const searchParams = { ...validQueryParams, ...params };
      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routeConfiguration, location);
      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams));
    };

    const onGuestSearch = params => {
      const merged = { ...validQueryParams, ...params };
      const searchParams = Object.fromEntries(Object.entries(merged).filter(([, v]) => v != null));
      const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(routeConfiguration, location);
      history.push(createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams));
    };

    const onFilterBarSubmit = params => {
      this.setState({ viewAllType: null });
      this.getHandleChangedValueFn(true)(params);
    };

    // sortBy for mobile filters drawer (popup variant)
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={validQueryParams[sortConfig.queryParamName]}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          labelId={`${mode}-search-page-sort-by`}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };

    // Raw sort data for the Filters panel chip row
    const currentSort = validQueryParams[sortConfig.queryParamName] || 'createdAt';
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Parse page heading to be included in the title
    const pageHeading = searchInProgress
      ? intl.formatMessage({ id: 'MainPanelHeader.loadingResults' })
      : intl.formatMessage({ id: 'MainPanelHeader.foundResults' }, { count: totalItems });

    const { bounds, origin } = searchParamsInURL || {};
    const { title, description, schema } = createSearchResultSchema(
      listings,
      searchParamsInURL || {},
      intl,
      routeConfiguration,
      config,
      pageHeading
    );

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={description}
        title={title}
        schema={schema}
      >
        {/* Standard topbar — visible on mobile only; hidden on desktop via CSS */}
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        {/* Mobile-only sticky Where/When/Who pill — sits just below the BoatersNetwork logo */}
        <MobileSearchBar
          address={parse(location.search)?.address}
          validQueryParams={validQueryParams}
          onLocationSearch={onLocationSearch}
          onSubmit={onFilterBarSubmit}
          onGuestSearch={onGuestSearch}
          intl={intl}
        />
        {/* Custom search-page topbar — desktop only */}
        <SearchPageTopbar
          address={parse(location.search)?.address}
          dates={validQueryParams?.dates}
          keywords={validQueryParams?.keywords}
          validQueryParams={validQueryParams}
          onSearch={params => {
            const merged = { ...validQueryParams, ...params };
            // Strip keys explicitly set to null/undefined (used to clear individual params).
            const searchParams = Object.fromEntries(
              Object.entries(merged).filter(([, v]) => v != null)
            );
            const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
              routeConfiguration,
              location
            );
            history.push(
              createResourceLocatorString(routeName, routeConfiguration, pathParams, searchParams)
            );
          }}
          onOpenFilters={() => this.setState({ shouldOpenFilterPanel: true })}
          currentUser={currentUser}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={onLogout}
          intl={intl}
          showCreateListingsLink={showCreateListingsLink}
          currentUserHasListings={currentUserHasListings}
        />
        <div id="main-content" className={css.container} role="main">
          {isMobileLayout && this.state.isSearchMapOpenOnMobile ? (
            <div className={css.mobileMapOverlay}>
              {shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={true}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {}}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          ) : null}
          {(() => {
            const searchFilterBar = (
              <SearchFilterBar
                  className={css.searchFilterBar}
                  validQueryParams={validQueryParams}
                  address={parse(location.search)?.address}
                  onSubmit={onFilterBarSubmit}
                  onGuestSearch={onGuestSearch}
                  defaultFiltersConfig={defaultFiltersConfig}
                  marketplaceCurrency={marketplaceCurrency}
                  initialValuesFn={initialValues(this.props, this.state.currentQueryParams)}
                  getHandleChangedValueFn={this.getHandleChangedValueFn}
                  listingCategories={listingCategories}
                  intl={intl}
                  totalItems={totalItems}
                  searchInProgress={searchInProgress}
                  onManageDisableScrolling={onManageDisableScrolling}
                  isMapVisible={this.state.isMapVisible}
                  onToggleMap={this.toggleMapVisibility}
                  onLocationSearch={onLocationSearch}
                  isMapOpen={this.state.isSearchMapOpenOnMobile}
                  onMobileMapOpen={() => this.setState({ isSearchMapOpenOnMobile: true })}
                  onMobileMapClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
                  isSortActive={sortConfig.active}
                  sortOptions={sortConfig.options}
                  sortQueryParamName={sortConfig.queryParamName}
                  currentSort={currentSort}
                  onSortSelect={this.handleSortBy}
                  externalOpen={this.state.shouldOpenFilterPanel}
                  onExternalOpenHandled={() => this.setState({ shouldOpenFilterPanel: false })}
                />
            );

            const searchFiltersMobile = (
              <SearchFiltersMobile
                className={css.searchFiltersMobileMap}
                urlQueryParams={validQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={totalItems}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onMapIconClick={() => this.setState({ isSearchMapOpenOnMobile: true })}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                noResultsInfo={noResultsInfo}
                location={location}
                isMapVariant
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
                  const filterId = `SearchFiltersMobile.${filterConfig.key.toLowerCase()}`;
                  return (
                    <FilterComponent
                      key={key}
                      id={filterId}
                      config={filterConfig}
                      containerId="SearchPage_MobileFilters"
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(this.props, this.state.currentQueryParams)}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
            );

            const listingsArea = isSecondaryFiltersOpen ? (
              <div className={classNames(css.searchFiltersPanel)}>
                <SearchFiltersSecondary
                  urlQueryParams={validQueryParams}
                  listingsAreLoaded={listingsAreLoaded}
                  applyFilters={this.applyFilters}
                  cancelFilters={this.cancelFilters}
                  resetAll={this.resetAll}
                  onClosePanel={() => this.setState({ isSecondaryFiltersOpen: false })}
                >
                  {customSecondaryFilters.map(filterConfig => {
                    const key = `SearchFiltersSecondary.${filterConfig.scope || 'built-in'}.${filterConfig.key}`;
                    const filterId = `SearchFiltersSecondary.${filterConfig.key.toLowerCase()}`;
                    return (
                      <FilterComponent
                        key={key}
                        id={filterId}
                        config={filterConfig}
                        containerId="SearchPageWithMap_SecondaryFilters"
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={validQueryParams}
                        initialValues={initialValues(this.props, this.state.currentQueryParams)}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showAsPopup={false}
                      />
                    );
                  })}
                </SearchFiltersSecondary>
              </div>
            ) : (
              <div
                className={classNames(css.listingsForMapVariant, {
                  [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
                })}
              >
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                <GroupedSearchResults
                    listings={listings}
                    setActiveListing={onActivateListing}
                    activeCategoryIds={(() => {
                      const raw = validQueryParams?.pub_categoryLevel1;
                      if (!raw) return [];
                      const s = raw.toString();
                      return s.startsWith('has_any:') ? s.slice(8).split(',').filter(Boolean) : [s];
                    })()}
                    expandedTabValue={this.state.viewAllType}
                    onViewAll={section => {
                      this.setState({ viewAllType: section.tabValue });
                      const catParam = section.categoryIds
                        ? (section.categoryIds.length === 1
                            ? section.categoryIds[0]
                            : `has_any:${section.categoryIds.join(',')}`)
                        : null;
                      this.getHandleChangedValueFn(true)({
                        pub_categoryLevel1: catParam,
                        pub_listingType: null,
                      });
                    }}
                    onBack={() => {
                      this.setState({ viewAllType: null });
                      this.getHandleChangedValueFn(true)({
                        pub_categoryLevel1: null,
                        pub_listingType: null,
                      });
                    }}
                    isMapHidden={!this.state.isMapVisible}
                    isMobile={isMobileLayout}
                    urlQueryParams={validQueryParams}
                    sectionListingsMap={this.props.sectionListingsMap}
                    onSearchSection={(sectionKey, sectionFilterParams) => {
                      const { config } = this.props;
                      const {
                        aspectWidth = 1,
                        aspectHeight = 1,
                        variantPrefix = 'listing-card',
                      } = config.layout.listingImage;
                      const aspectRatio = aspectHeight / aspectWidth;
                      this.props.onSearchSection(sectionKey, {
                        ...sectionFilterParams,
                        perPage: 100,
                        include: ['author', 'images'],
                        'fields.listing': [
                          'title',
                          'geolocation',
                          'price',
                          'deleted',
                          'state',
                          'publicData.listingType',
                          'publicData.categoryLevel1',
                          'publicData.categoryLevel2',
                          'publicData.categoryLevel3',
                          'publicData.transactionProcessAlias',
                          'publicData.unitType',
                          'publicData.cardStyle',
                          'publicData.pickupEnabled',
                          'publicData.shippingEnabled',
                          'publicData.priceVariationsEnabled',
                          'publicData.priceVariants',
                          'publicData.minimum_hours',
                          'publicData.available_slot_durations',
                          'publicData.rental_slots',
                          'publicData.multi_day_slots',
                          'publicData.hourly_booking_type',
                          'publicData.multi_day_booking_type',
                          'publicData.recommended_captains',
                          'publicData.recommended_delivery_providers',
                        ],
                        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
                        'fields.image': [
                          'variants.scaled-small',
                          'variants.scaled-medium',
                          `variants.${variantPrefix}`,
                          `variants.${variantPrefix}-2x`,
                        ],
                        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
                        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
                        'limit.images': 1,
                      }, config);
                    }}
                    onClearSection={this.props.onClearSection}
                  />
              </div>
            );

            if (isMobileLayout && this.state.isSearchMapOpenOnMobile) {
              // Mobile map open: proper bottom sheet — does NOT overlay the map area
              return (
                <div
                  className={css.mobileBottomSheet}
                  ref={node => { this._sheetRef = node; }}
                >
                  <div
                    className={css.mobileBottomSheetHeader}
                    onTouchStart={this.onSheetHeaderTouchStart}
                    onTouchMove={this.onSheetHeaderTouchMove}
                    onTouchEnd={this.onSheetHeaderTouchEnd}
                  >
                    <div className={css.mapDrawerHandle} aria-hidden="true" />
                  </div>
                  <div
                    className={css.mobileBottomSheetBody}
                    ref={node => { this._sheetBodyRef = node; }}
                    onTouchStart={this.onSheetBodyTouchStart}
                    onTouchMove={this.onSheetBodyTouchMove}
                    onTouchEnd={this.onSheetBodyTouchEnd}
                  >
                    {searchFilterBar}
                    {searchFiltersMobile}
                    <MainPanelHeader
                      className={css.mainPanelMapVariant}
                      noResultsInfo={noResultsInfo}
                    />
                    {listingsArea}
                  </div>
                </div>
              );
            }

            // Normal (non-map) layout
            return (
              <div className={css.leftPanel}>
                {searchFilterBar}
                {searchFiltersMobile}
                <MainPanelHeader
                  className={css.mainPanelMapVariant}
                  noResultsInfo={noResultsInfo}
                />
                {listingsArea}
              </div>
            );
          })()}
          <ModalInMobile
            className={classNames(css.mapPanel, { [css.mapPanelHidden]: !this.state.isMapVisible })}
            id="SearchPage_map"
            isModalOpenOnMobile={false}
            onClose={() => this.setState({ isSearchMapOpenOnMobile: false })}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onManageDisableScrolling={onManageDisableScrolling}
          >
            <div className={css.mapWrapper} data-testid="searchMapContainer">
              {!isMobileLayout && shouldShowSearchMap ? (
                <SearchMap
                  reusableContainerClassName={css.map}
                  rootClassName={css.mapRoot}
                  activeListingId={activeListingId}
                  bounds={bounds}
                  center={origin}
                  isSearchMapOpenOnMobile={false}
                  location={location}
                  listings={listings || []}
                  onMapMoveEnd={this.onMapMoveEnd}
                  onCloseAsModal={() => {
                    onManageDisableScrolling('SearchPage_map', false);
                  }}
                  messages={intl.messages}
                />
              ) : null}
            </div>
          </ModalInMobile>
        </div>
        {!(isMobileLayout && this.state.isSearchMapOpenOnMobile) ? (
          <FooterContainer />
        ) : null}
      </Page>
    );
  }
}

/**
 * SearchPage component with map layout
 *
 * @param {Object} props
 * @param {propTypes.uuid} [props.activeListingId] - The active listing ID
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onActivateListing] - The function to activate a listing
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    currentUser,
    currentUserHasListings,
    currentUserSaleNotificationCount = 0,
    currentUserOrderNotificationCount = 0,
  } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  const rawSectionResults = state.SearchPage.sectionResults || {};
  const sectionListingsMap = Object.fromEntries(
    Object.entries(rawSectionResults).map(([key, result]) => [
      key,
      {
        listings: getListingsById(state, result.ids || []),
        inProgress: result.inProgress || false,
        error: result.error || null,
      },
    ])
  );

  return {
    currentUser,
    currentUserHasListings,
    isAuthenticated,
    notificationCount: currentUserSaleNotificationCount + currentUserOrderNotificationCount,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    sectionListingsMap,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
  onLogout: () => dispatch(logout()),
  onSearchSection: (sectionKey, searchParams, config) =>
    dispatch(searchSectionListings({ sectionKey, searchParams, config })),
  onClearSection: sectionKey => dispatch(clearSectionResults(sectionKey)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedSearchPage);

export default SearchPage;
